import { useState, useContext } from "react";
import { Button } from '@chakra-ui/react';
import SubmitOfferModal from "./SubmitOfferModal";
import CancelOfferConfirmationModal from "./CancelOfferConfirmationModal";
import { ProfileContext } from "../../../../store/ProfileProvider";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  tradeDetail: any;
  offerDetail: any;
  setSwalProps: any;
};

const TicketOfferDetailsAccordion: React.FC<Props> = ({ tradeDetail, offerDetail, setSwalProps }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isOpen, setIsOpen] = useState(true);
  const [isOpenReissueOfferModal, setIsOpenReissueOfferModal] = useState(false);
  const [isOpenCancelOfferModal, setIsOpenCancelOfferModal] = useState(false);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const formatDate = (timestamp: string) => {
    // return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM, YYYY hh:mm A");
    return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM, YYYY");
  };

  const displayStatus = (status: string) => {
    switch (status) {
      case "pending":
        return <span className="status-pending">Pending Acceptance</span>;
      case "accepted":
        return <span className="status-accepted">Accepted</span>;
      case "cancelled":
        return <span className="status-cancelled">Cancelled</span>;
      case "expired":
        return <span className="text-amber-500">Expired</span>;
      default:
        return <span className="text-gray-500">{status}</span>;
    }
  };

  const handleOpenReissueOfferModal = () => {
    setIsOpenReissueOfferModal(true);
  };

  const handleCloseReissueOfferModal = () => {
    setIsOpenReissueOfferModal(false);
  };

  const handleOpenCancelOfferModal = () => {
    setIsOpenCancelOfferModal(true);
  };

  const handleCloseCancelOfferModal = () => {
    setIsOpenCancelOfferModal(false);
  };

  return (
    <>
      <SubmitOfferModal
        tradeDetail={tradeDetail}
        isOpen={isOpenReissueOfferModal}
        onClose={handleCloseReissueOfferModal}
        setSwalProps={setSwalProps}
        isReissue={true}
        previousOfferId={offerDetail.status === "pending" && offerDetail.offer_id}
        previousOfferStatus={offerDetail.status}
      />

      {userRole === "superadmin" && offerDetail && isOpenCancelOfferModal &&
        <CancelOfferConfirmationModal
          tradeDetail={tradeDetail}
          offerDetail={offerDetail}
          isOpen={isOpenCancelOfferModal}
          onClose={handleCloseCancelOfferModal}
          setSwalProps={setSwalProps}
        />
      }

      <div className="card mb-4 !rounded-xl !border-0 p-4">
        <button
          className="flex w-full items-center justify-between text-sm font-bold text-gray-700"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-primary-500">Current Offer</span>
          <svg
            className={`h-4 w-4 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""
              }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && (
          <div>
            <div className={`${offerDetail.status !== "accepted" && userRole === "superadmin" ? "mb-4" : ""} mt-4 grid grid-cols-[auto,1fr] gap-x-2 gap-y-2 text-sm text-gray-700`}>
              <span>Created Date</span>
              <span className="text-right font-medium text-navy-700">
                {formatDate(offerDetail.offer_id)}
              </span>
              <span>Sell</span>
              <span className="text-right font-medium text-navy-700">
                <DisplayAmountWithDecimals
                  amount={Number(offerDetail.sell)}
                  currency={offerDetail.sell_asset}
                  decimal={tradeDetail?.from_asset_decimal}
                />
              </span>
              <span>Sell Asset</span>
              <span className="text-right font-medium text-navy-700">{offerDetail.sell_asset}</span>
              <span>Receive</span>
              <span className="text-right font-medium text-navy-700">
                <DisplayAmountWithDecimals
                  amount={Number(offerDetail.receive)}
                  currency={offerDetail.receive_asset}
                  decimal={tradeDetail?.to_asset_decimal}
                />
              </span>
              <span>Receive Asset</span>
              <span className="text-right font-medium text-navy-700">{offerDetail.receive_asset}</span>
              <span>Rate</span>
              <span className="text-right font-medium text-navy-700">
                <DisplayAmountWithDecimals
                  amount={Number(offerDetail.rate)}
                  currency={offerDetail.sell_asset}
                  decimal={tradeDetail?.from_asset_decimal}
                />
              </span>
              <span>Offer Status</span>
              <span className="text-right font-medium">{displayStatus(offerDetail.status)}</span>
            </div>
            {tradeDetail.status === "awaiting" && offerDetail.status !== "accepted" && userRole === "superadmin" &&
              <div className="mt-6 flex flex-col gap-3 md:flex-row">
                {offerDetail.status === "pending" &&
                  <Button onClick={handleOpenCancelOfferModal} className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-gray-50">
                    Cancel
                  </Button>
                }
                <Button onClick={handleOpenReissueOfferModal} className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-primary-500 hover:bg-gray-50">
                  Reissue
                </Button>
              </div>
            }
          </div>
        )}
      </div>
    </>
  );
}

export default TicketOfferDetailsAccordion;